import React from 'react'
import { StyleSheet, Text, View } from 'react-native'
import { Panel } from '../component/da-panel'
import { useTranslation } from 'react-i18next'
import { AppContext, assets, CheckboxTable, CheckboxTableColum, ErrorHandling, SortFieldInfo, TaskDetail } from '@pulseops/common'
import { pipe } from 'fp-ts/lib/function'
import { ZIO } from "@mxt/zio"
import { DAAdditionalInformationService } from './da-additional-information-service'
import { useLoading } from '@mxt/zio-react'
import { FieldDescription, FieldText, SectionCol, SectionRow } from '@pulseops/submission/common'
import { useIsFocused } from '@react-navigation/native'
import { Link } from '@material-ui/core'

type DAAdditionalInformationProps = {
  detail: TaskDetail.AgentOnboardNew
}
export const DAAdditionalInformation = (props: DAAdditionalInformationProps) => {
  const { t } = useTranslation('RequestInfo')
  const [isLoading, bindLoader] = useLoading(false)
  const { showGlobalLoading } = React.useContext(AppContext.AppContextInstance)
  const [sortItem, setSortItem] = React.useState<SortFieldInfo>({
    field: '',
    order: "asc"
  })
  const isFocused = useIsFocused()
  const displayedColumns: CheckboxTableColum[] = [
    {
      title: t('requestInfo:Type'),
      fieldName: 'fieldTypeDisplay',
      disabled: true,
      maxWidth: 200
    },
    {
      title: t('EKYD'),
      fieldName: 'ekyd',
      disabled: true,
      maxWidth: 400,
      rowRender: (selectedRow) => {
        let dataUri = ''
        const selectedValue = selectedRow.selectItem["ekyd"] as string
        const displayedFieldName = selectedRow.selectItem["fieldTypeEKYD"] as string
        const isShowedHyperLink = displayedFieldName === 'portrait' && !!selectedValue
        if (isShowedHyperLink) {
          dataUri = `data:${getImageType(selectedValue)};base64,${selectedValue}`
        }
        return (
          isShowedHyperLink
            ? <Link href={dataUri} target={"_parent"} download><Text style={additionalStyles.hyperLinkText}>{t('View_DownloadImage')}</Text></Link>
            : <Text>{selectedValue}</Text>
        )
      }
    },
    {
      title: t('Submission'),
      fieldName: 'submission',
      disabled: true,
      maxWidth: 200
    },
    {
      title: t('ResultMatching'),
      fieldName: 'resultMatching',
      disabled: true,
      maxWidth: 200,
      rowRender: (selectedRow) => {
        const resultMatchingVal = selectedRow.selectItem["resultMatching"] as string
        switch (resultMatchingVal) {
          case 'PASS':
            return <assets.DAPassedIcon />
          case 'FAIL':
            return <assets.DAFailedIcon />
          default:
            return <assets.DAUndefinedIcon />
        }
      }
    }
  ]

  React.useEffect(() => { showGlobalLoading(isLoading) }, [isLoading])

  const additionalInfoData = pipe(
    isFocused ? DAAdditionalInformationService.getAdditionalInformationFromPruforce(props.detail.businessKey || '')
      : ZIO.succeed(null),
    ZIO.map((response) => {
      return {
        ...response,
        ekydInfos: response && response.ekydInfos ? response?.ekydInfos.map((item) => {
          let fieldName = item.fieldTypeDisplay.replace(/\s+/g, '')
          switch (fieldName) {
            case 'Gender':
              fieldName = t(`submission:${fieldName}`)
              break
            case 'IssueDate':
              fieldName = t('DAIssueDate')
              break
            case 'Name':
              fieldName = t('ADName')
              break
            default:
              fieldName = t(`${fieldName}`)
          }
          return {
            ...item, fieldTypeDisplay: fieldName
          }
        }) : []
      }
    }),
    bindLoader,
    ErrorHandling.runDidUpdate([isFocused])
  )

  const getImageType = (base64Str: string) => {
    let imageType = ''
    if (base64Str.startsWith('iVBOR')) {
      imageType = "image/png"
    } else if (base64Str.startsWith("/9j/")) {
      imageType = "image/jpeg"
    } else if (base64Str.startsWith("R0lGOD")) {
      imageType = "image/gif"
    } else {
      imageType = ''
    }
    return imageType
  }

  return (
    <View>
      <Panel title={t('EKYDInformation')} isExand={true}>
        <CheckboxTable
          displayedColumns={displayedColumns}
          dataSource={additionalInfoData?.ekydInfos || []}
          stickyHeader
          isPagination={false}
          sortItem={sortItem}
          setSortItem={setSortItem}
          containerStyle={additionalStyles.tableContainer}
        />
      </Panel>
      <Panel title={t('EKYDResult')}>
        <SectionRow>
          <SectionCol>
            <FieldText text={t('Integrity')}></FieldText>
            <FieldDescription text={additionalInfoData?.ekydVerifyInfo?.integrity || ''}></FieldDescription>
          </SectionCol>
          <SectionCol>
            <FieldText text={t('CCCDAuthentication')}></FieldText>
            <FieldDescription text={additionalInfoData?.ekydVerifyInfo?.authenticationCCCD || ''}></FieldDescription>
          </SectionCol>
          <SectionCol>
            <FieldText text={t('FaceMatching')}></FieldText>
            <FieldDescription text={additionalInfoData?.ekydVerifyInfo?.faceMatchingResult || ''}></FieldDescription>
          </SectionCol>
        </SectionRow>
      </Panel>
      <Panel title={t('DAInterviewResult')}>
        <SectionRow>
          <SectionCol>
            <FieldText text={t('FaceMatchingResult')}></FieldText>
            <FieldDescription text={additionalInfoData?.interviewInfo?.faceMatchingResult || ''}></FieldDescription>
          </SectionCol>
          <SectionCol>
            <FieldText text={t('InterviewTime')}></FieldText>
            <FieldDescription text={additionalInfoData?.interviewInfo?.interviewTime || ''}></FieldDescription>
          </SectionCol>
          <SectionCol>
            <FieldText text={t('InterviewerFullName')}></FieldText>
            <FieldDescription text={additionalInfoData?.interviewInfo?.interviewerFullName || ''}></FieldDescription>
          </SectionCol>
        </SectionRow>
        <SectionRow isSecondLine>
          <SectionCol>
            <FieldText text={t('TimeUpdateResultInterview')}></FieldText>
            <FieldDescription text={additionalInfoData?.interviewInfo?.timeUpdateResult || ''}></FieldDescription>
          </SectionCol>
          <SectionCol>
            <FieldText text={t('InterviewResult')}></FieldText>
            <FieldDescription text={additionalInfoData?.interviewInfo?.interviewResult || ''}></FieldDescription>
          </SectionCol>
        </SectionRow>
      </Panel>
      <Panel title={t('CandidateEvaluationInformation')}>
        <SectionRow>
          <SectionCol>
            <FieldText text={t('AgentLeaderFullname')}></FieldText>
            <FieldDescription text={additionalInfoData?.candidateAssessmentInfo?.agentLeaderFullName || ''}></FieldDescription>
          </SectionCol>
          <SectionCol>
            <FieldText text={t('ApproveDateOfAgentLeader')}></FieldText>
            <FieldDescription text={additionalInfoData?.candidateAssessmentInfo?.approveDateOfAgentLeader || ''}></FieldDescription>
          </SectionCol>
          <SectionCol>
            <FieldText text={t('ApproveStatusOfAgentLeader')}></FieldText>
            <FieldDescription text={additionalInfoData?.candidateAssessmentInfo?.approveStatusOfAgentLeader || ''}></FieldDescription>
          </SectionCol>
        </SectionRow>
        <SectionRow isSecondLine>
          <SectionCol>
            <FieldText text={t('ADInterviewerFullName')}></FieldText>
            <FieldDescription text={additionalInfoData?.candidateAssessmentInfo?.interviewerFullName || ''}></FieldDescription>
          </SectionCol>
          <SectionCol>
            <FieldText text={t('ApproveDateOfBDM')}></FieldText>
            <FieldDescription text={additionalInfoData?.candidateAssessmentInfo?.approveDateOfBDM || ''}></FieldDescription>
          </SectionCol>
          <SectionCol>
            <FieldText text={t('ApproveStatusOfBDM')}></FieldText>
            <FieldDescription text={additionalInfoData?.candidateAssessmentInfo?.approveStatusOfBDM || ''}></FieldDescription>
          </SectionCol>
        </SectionRow>
        <SectionRow isSecondLine>
          <SectionCol>
            <FieldText text={t('ReapproveDateByBDM')}></FieldText>
            <FieldDescription text={additionalInfoData?.candidateAssessmentInfo?.reApproveDateByBDM || ''}></FieldDescription>
          </SectionCol>
          <SectionCol>
            <FieldText text={t('ReapproveStatusByBDM')}></FieldText>
            <FieldDescription text={additionalInfoData?.candidateAssessmentInfo?.reApproveStatusByBDM || ''}></FieldDescription>
          </SectionCol>
          <SectionCol>
            <FieldText text={t('ApproveCandidateWithoutAHighSchoolDiploma')}></FieldText>
            <FieldDescription text={additionalInfoData?.candidateAssessmentInfo?.approveNotHaveDiploma || ''}></FieldDescription>
          </SectionCol>
        </SectionRow>
        <SectionRow isSecondLine>
          <SectionCol>
            <FieldText text={t('Comment')}></FieldText>
            <FieldDescription text={additionalInfoData?.candidateAssessmentInfo?.comment || ''}></FieldDescription>
          </SectionCol>
        </SectionRow>
      </Panel>
    </View>
  )
}
const additionalStyles = StyleSheet.create({
  container: {
    // display: 'flex',
  },
  tableContainer: {
    marginVertical: 0
  },
  hyperLinkText: {
    cursor: 'pointer',
    color: 'blue',
    textDecorationLine: 'underline',
    fontWeight: 'bold'
  }
})